/**
 * QuizStudent.jsx — Vue élève (mobile-first) pour le Quiz d'ancrage.
 * URL : /quiz/:code
 * L'élève :
 *   1. arrive via scan QR (ou tape le code)
 *   2. répond aux N questions (A/B/C/D)
 *   3. voit son score à la fin
 * Anonyme : participant_id stocké en localStorage pour permettre de reprendre.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Check, X as XIcon, Trophy, Sparkles, UserPlus, ArrowRight, Award, Target } from 'lucide-react';
import { CartePermisAM } from '../components/CartePermisAM';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getParticipantId() {
  let id = localStorage.getItem('c2r_quiz_pid');
  if (!id) {
    id = 'p_' + Math.random().toString(36).slice(2, 10);
    localStorage.setItem('c2r_quiz_pid', id);
  }
  return id;
}

export default function QuizStudent() {
  const { code } = useParams();
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({}); // { qIndex: { answer, correct } }
  const [submitted, setSubmitted] = useState(false);
  const [cartePrenom, setCartePrenom] = useState('');
  const [carteNom, setCarteNom] = useState('');
  const [showCarte, setShowCarte] = useState(false);
  const pid = getParticipantId();

  // Cérémonie : afficher automatiquement la carte permis dès la fin du QCM
  useEffect(() => {
    if (submitted && session?.quiz_id === 'ceremonie-am') {
      setShowCarte(true);
    }
  }, [submitted, session?.quiz_id]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/quiz/sessions/${code}`);
        if (!res.ok) throw new Error('Session introuvable ou terminée');
        const data = await res.json();
        setSession(data);
      } catch (e) {
        setError(e.message);
      }
    })();
  }, [code]);

  const submitAnswer = async (choice) => {
    if (answers[current] !== undefined) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/quiz/sessions/${code}/answers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ participant_id: pid, question_index: current, answer: choice }),
      });
      const data = await res.json();
      setAnswers((a) => ({ ...a, [current]: { answer: choice, correct: data.correct, correct_answer: data.correct_answer } }));
    } catch (e) {
      alert("Erreur : " + e.message);
    }
  };

  const next = () => {
    if (current + 1 >= session.questions.length) setSubmitted(true);
    else setCurrent((c) => c + 1);
  };

  // Confettis Or : déclenchés une fois quand l'élève termine avec ≥80%
  useEffect(() => {
    if (!submitted || !session) return;
    const correctCount = Object.values(answers).filter((a) => a.correct).length;
    const total = session.questions.length;
    const pct = total > 0 ? Math.round((correctCount / total) * 100) : 0;
    if (pct < 80) return;

    let cancelled = false;
    import('canvas-confetti').then(({ default: confetti }) => {
      if (cancelled) return;
      const duration = 3200;
      const end = Date.now() + duration;
      const colors = ['#fbbf24', '#f59e0b', '#facc15', '#fde047', '#ef4444', '#ffffff'];
      // Salve initiale centrale
      confetti({
        particleCount: 140,
        spread: 90,
        origin: { y: 0.35 },
        colors,
        scalar: 1.1,
      });
      // Salves latérales continues façon feu d'artifice
      (function frame() {
        if (cancelled) return;
        confetti({
          particleCount: 4,
          angle: 60,
          spread: 60,
          startVelocity: 55,
          origin: { x: 0, y: 0.8 },
          colors,
        });
        confetti({
          particleCount: 4,
          angle: 120,
          spread: 60,
          startVelocity: 55,
          origin: { x: 1, y: 0.8 },
          colors,
        });
        if (Date.now() < end) requestAnimationFrame(frame);
      })();
    });
    return () => { cancelled = true; };
  }, [submitted, session]);

  if (error) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-4">
        <div className="max-w-sm rounded-xl bg-red-500/10 border border-red-500 p-6 text-center">
          <XIcon className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <div className="font-bold text-red-400">{error}</div>
          <div className="text-xs text-muted-foreground mt-2">Demande à ton moniteur un nouveau code.</div>
        </div>
      </div>
    );
  }

  if (!session) {
    return <div className="min-h-screen bg-background text-foreground flex items-center justify-center">Chargement…</div>;
  }

  if (submitted) {
    const correctCount = Object.values(answers).filter((a) => a.correct).length;
    const total = session.questions.length;
    const pct = total > 0 ? Math.round((correctCount / total) * 100) : 0;
    const isGold = pct >= 80;
    const isSilver = pct >= 60 && pct < 80;
    const isBronze = pct < 60;
    const badgeLabel = isGold ? 'Or' : isSilver ? 'Argent' : 'À retravailler';
    const badgeColor = isGold ? 'text-yellow-300 border-yellow-400/60 bg-yellow-400/10'
      : isSilver ? 'text-slate-200 border-slate-300/60 bg-slate-300/10'
      : 'text-orange-300 border-orange-400/60 bg-orange-400/10';
    const feedback = isGold
      ? 'Excellent — tu as compris les points clés du cours.'
      : isSilver
      ? 'Solide début — quelques notions à revoir pour être vraiment au point.'
      : 'Pas de panique — revois les sections concernées avec ton moniteur.';

    // SVG progress ring
    const ringSize = 160;
    const stroke = 12;
    const radius = (ringSize - stroke) / 2;
    const circ = 2 * Math.PI * radius;
    const dash = (pct / 100) * circ;

    const returnUrl = `/quiz/${code}`;
    const registerHref = `/register?return=${encodeURIComponent(returnUrl)}`;
    const isCeremonie = session.quiz_id === 'ceremonie-am' || session.quiz_title?.toLowerCase().includes('cérémonie');
    const canGenerate = cartePrenom.trim().length >= 2 && carteNom.trim().length >= 2;

    // ═══════ Vue "carte permis" cérémonie — affichée AUTOMATIQUEMENT à la fin du QCM ═══════
    if (isCeremonie && showCarte) {
      const displayPrenom = cartePrenom.trim() || 'Élève';
      const displayNom = carteNom.trim() || 'Cyclomotoriste';
      const hasCustomName = cartePrenom.trim().length >= 2 && carteNom.trim().length >= 2;
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-black text-white p-4 flex flex-col items-center justify-center relative overflow-hidden" data-testid="quiz-carte-page">
          <div className="max-w-md w-full py-4 relative z-10">
            <CartePermisAM
              nom={displayNom}
              prenom={displayPrenom}
              confettis
              showTitle
              showMention
              compact
            />

            {/* Bouton pour personnaliser son nom */}
            <div className="mt-5 relative z-20">
              {!hasCustomName ? (
                <div className="rounded-2xl border-2 border-yellow-500/40 bg-yellow-500/10 backdrop-blur p-4" data-testid="quiz-carte-edit-form">
                  <div className="text-[10px] font-black uppercase tracking-widest text-yellow-300 mb-2 text-center">
                    ✨ Personnalise ta carte permis
                  </div>
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={cartePrenom}
                      onChange={(e) => setCartePrenom(e.target.value)}
                      placeholder="Prénom (ex : Léa)"
                      maxLength={30}
                      className="w-full rounded-xl bg-black/50 border-2 border-white/20 focus:border-yellow-500 outline-none px-3 py-2.5 text-white text-sm font-black placeholder:text-white/30 placeholder:font-normal"
                      data-testid="quiz-input-prenom"
                    />
                    <input
                      type="text"
                      value={carteNom}
                      onChange={(e) => setCarteNom(e.target.value)}
                      placeholder="Nom (ex : DUPONT)"
                      maxLength={30}
                      className="w-full rounded-xl bg-black/50 border-2 border-white/20 focus:border-yellow-500 outline-none px-3 py-2.5 text-white text-sm font-black uppercase placeholder:text-white/30 placeholder:normal-case placeholder:font-normal"
                      data-testid="quiz-input-nom"
                    />
                  </div>
                  <div className="mt-2 text-[10px] text-center text-white/60">
                    Ta carte se met à jour en temps réel
                  </div>
                </div>
              ) : (
                <div className="text-center flex flex-col gap-2 items-center">
                  <button
                    type="button"
                    onClick={() => { setCartePrenom(''); setCarteNom(''); }}
                    className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-3 py-1.5 transition"
                    data-testid="quiz-carte-edit-name"
                  >
                    ✏️ Modifier mon nom
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-background text-foreground p-4 flex items-center justify-center" data-testid="quiz-result">
        <div className="max-w-md w-full space-y-4">
          {/* Carte score */}
          <div className="rounded-3xl bg-gradient-to-br from-card via-card to-red-950/20 border-2 border-red-500/25 p-6 text-center shadow-2xl">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest mb-4 border-2 bg-red-500/10 border-red-500/40 text-red-300">
              <Sparkles className="w-3 h-3" /> Quiz terminé
            </div>

            {/* Ring circulaire animé */}
            <div className="relative mx-auto mb-4" style={{ width: ringSize, height: ringSize }}>
              <svg width={ringSize} height={ringSize} className="-rotate-90">
                <circle
                  cx={ringSize / 2}
                  cy={ringSize / 2}
                  r={radius}
                  stroke="rgba(255,255,255,0.08)"
                  strokeWidth={stroke}
                  fill="none"
                />
                <circle
                  cx={ringSize / 2}
                  cy={ringSize / 2}
                  r={radius}
                  stroke={isGold ? '#facc15' : isSilver ? '#e2e8f0' : '#fb923c'}
                  strokeWidth={stroke}
                  fill="none"
                  strokeDasharray={circ}
                  strokeDashoffset={circ - dash}
                  strokeLinecap="round"
                  style={{ transition: 'stroke-dashoffset 800ms ease-out' }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-5xl font-black tabular-nums leading-none" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  {correctCount}
                  <span className="text-2xl text-muted-foreground">/{total}</span>
                </div>
                <div className="mt-1 text-xs font-black uppercase tracking-widest text-muted-foreground tabular-nums">
                  {pct}%
                </div>
              </div>
            </div>

            {/* Badge */}
            <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-widest mb-3 border-2 ${badgeColor}`} data-testid="quiz-badge">
              <Trophy className="w-3.5 h-3.5" /> Médaille {badgeLabel}
            </div>

            <h2 className="text-2xl font-black mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Bravo, quiz terminé !
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {feedback}
            </p>
            <div className="mt-3 text-xs text-muted-foreground/80 italic">
              Tes réponses ont été enregistrées et s&apos;affichent au tableau.
            </div>
          </div>

          {/* Formulaire nom/prénom pour Cérémonie AM (auto-carte permis) */}
          {isCeremonie && (
            <div className="rounded-3xl border-2 border-yellow-500/50 bg-gradient-to-br from-yellow-600/25 via-amber-500/15 to-transparent p-5 sm:p-6 shadow-2xl" data-testid="quiz-carte-form">
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-yellow-500/25 border border-yellow-500/60 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-yellow-300" />
                </div>
                <div className="flex-1">
                  <div className="text-[10px] font-black uppercase tracking-[0.22em] text-yellow-300 mb-1">
                    🏆 Cérémonie de fin de formation
                  </div>
                  <h3 className="text-lg font-black leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    Génère ta carte permis AM
                  </h3>
                </div>
              </div>

              <p className="text-sm text-foreground/85 leading-relaxed mb-4">
                Saisis ton <strong className="text-yellow-300">nom et prénom</strong> pour obtenir ta carte permis AM personnalisée (visuel de cérémonie).
              </p>

              <div className="space-y-2 mb-4">
                <input
                  type="text"
                  value={cartePrenom}
                  onChange={(e) => setCartePrenom(e.target.value)}
                  placeholder="Prénom (ex : Léa)"
                  maxLength={30}
                  className="w-full rounded-xl bg-black/40 border-2 border-white/20 focus:border-yellow-500 outline-none px-4 py-3 text-white font-black tracking-wide placeholder:text-white/30 placeholder:font-normal"
                  data-testid="quiz-input-prenom"
                />
                <input
                  type="text"
                  value={carteNom}
                  onChange={(e) => setCarteNom(e.target.value)}
                  placeholder="Nom (ex : DUPONT)"
                  maxLength={30}
                  className="w-full rounded-xl bg-black/40 border-2 border-white/20 focus:border-yellow-500 outline-none px-4 py-3 text-white font-black uppercase tracking-wide placeholder:text-white/30 placeholder:normal-case placeholder:font-normal"
                  data-testid="quiz-input-nom"
                />
              </div>

              <button
                type="button"
                onClick={() => canGenerate && setShowCarte(true)}
                disabled={!canGenerate}
                className={`w-full inline-flex items-center justify-center gap-2 rounded-xl font-black uppercase tracking-widest py-3.5 text-sm transition-all ${
                  canGenerate
                    ? 'bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-400 hover:to-amber-500 text-black active:scale-[0.98] shadow-lg'
                    : 'bg-white/10 text-white/40 cursor-not-allowed'
                }`}
                data-testid="quiz-generate-carte"
              >
                <Sparkles className="w-4 h-4" /> Générer ma carte permis
                <ArrowRight className="w-4 h-4" />
              </button>

              <div className="mt-3 text-[11px] text-center text-white/60 leading-relaxed">
                <strong className="text-yellow-300/90">⚠</strong> Visuel de cérémonie uniquement · l&apos;attestation officielle est délivrée par ton auto-école.
              </div>
            </div>
          )}

          {/* Carte inscription — Garder mes résultats (masqué en mode cérémonie) */}
          {!isCeremonie && (
          <div className="rounded-3xl border-2 border-red-500/40 bg-gradient-to-br from-red-600/20 via-red-500/8 to-transparent p-5 sm:p-6" data-testid="quiz-signup-cta">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-red-500/20 border border-red-500/40 flex items-center justify-center">
                <Award className="w-5 h-5 text-red-300" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-300 mb-1">
                  Garde tes résultats
                </div>
                <h3 className="text-lg font-black leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  Crée ton compte Code 2 Roues
                </h3>
              </div>
            </div>

            <p className="text-sm text-foreground/85 leading-relaxed mb-4">
              Tu es peut-être un <strong className="text-red-300">futur élève moto ou scooter</strong> —
              en créant ton compte, tu conserves ce score, tu suis ta progression et tu accèdes aux
              cours, livrets interactifs et quiz d&apos;entraînement.
            </p>

            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="p-2.5 rounded-lg bg-black/25 border border-white/8 text-center">
                <Target className="w-4 h-4 text-red-300 mx-auto mb-1" />
                <div className="text-[10px] font-black uppercase tracking-wider text-muted-foreground leading-tight">
                  Score<br />sauvegardé
                </div>
              </div>
              <div className="p-2.5 rounded-lg bg-black/25 border border-white/8 text-center">
                <Sparkles className="w-4 h-4 text-red-300 mx-auto mb-1" />
                <div className="text-[10px] font-black uppercase tracking-wider text-muted-foreground leading-tight">
                  Livrets<br />interactifs
                </div>
              </div>
              <div className="p-2.5 rounded-lg bg-black/25 border border-white/8 text-center">
                <Trophy className="w-4 h-4 text-red-300 mx-auto mb-1" />
                <div className="text-[10px] font-black uppercase tracking-wider text-muted-foreground leading-tight">
                  Progression<br />visible
                </div>
              </div>
            </div>

            <Link
              to={registerHref}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 hover:bg-red-500 active:scale-[0.98] text-white font-black uppercase tracking-widest py-3.5 text-sm transition-all"
              data-testid="quiz-signup-btn"
            >
              <UserPlus className="w-4 h-4" /> Créer mon compte
              <ArrowRight className="w-4 h-4" />
            </Link>

            <div className="mt-3 text-[11px] text-center text-muted-foreground">
              Déjà un compte ?{' '}
              <Link
                to={`/login?return=${encodeURIComponent(returnUrl)}`}
                className="text-red-300 hover:text-red-200 underline underline-offset-2 font-bold"
                data-testid="quiz-login-link"
              >
                Se connecter
              </Link>
            </div>

            <div className="mt-4 pt-4 border-t border-white/10 text-[11px] text-muted-foreground text-center leading-relaxed">
              <strong className="text-foreground/90">Parents :</strong> ce compte permet aussi de suivre
              la progression de votre enfant tout au long de la formation.
            </div>
          </div>
          )}
        </div>
      </div>
    );
  }

  const q = session.questions[current];
  const ans = answers[current];
  const total = session.questions.length;
  const progressPct = (current / total) * 100;

  // Palette 4 choix — style plateau live (Kahoot-like) adaptée à la charte C2R
  const CHOICE_STYLES = [
    { bg: 'from-red-600 to-red-700',       border: 'border-red-400',      text: 'text-white', shape: '▲', name: 'Rouge' },
    { bg: 'from-blue-600 to-blue-700',     border: 'border-blue-400',     text: 'text-white', shape: '◆', name: 'Bleu' },
    { bg: 'from-amber-500 to-orange-600',  border: 'border-amber-300',    text: 'text-white', shape: '●', name: 'Ambre' },
    { bg: 'from-emerald-600 to-emerald-700', border: 'border-emerald-400', text: 'text-white', shape: '■', name: 'Vert' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-red-950/10 text-foreground p-4 pb-8" data-testid="quiz-student-page">
      <div className="max-w-md mx-auto">
        {/* En-tête compact avec titre + compteur circulaire */}
        <div className="flex items-center gap-3 mb-5">
          {/* Cercle progression */}
          <div className="relative flex-shrink-0" style={{ width: 56, height: 56 }}>
            <svg width="56" height="56" className="-rotate-90">
              <circle cx="28" cy="28" r="24" stroke="rgba(255,255,255,0.08)" strokeWidth="4" fill="none" />
              <circle
                cx="28" cy="28" r="24"
                stroke="#ef4444"
                strokeWidth="4"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 24}
                strokeDashoffset={2 * Math.PI * 24 * (1 - progressPct / 100)}
                style={{ transition: 'stroke-dashoffset 400ms ease-out' }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-sm font-black tabular-nums leading-none">
                <span className="text-red-400">{current + 1}</span>
                <span className="text-[10px] text-muted-foreground">/{total}</span>
              </div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-red-400 mb-0.5 truncate">
              Quiz d&apos;ancrage
            </div>
            <div className="text-xs font-bold text-foreground/90 leading-tight truncate" title={session.quiz_title}>
              {session.quiz_title}
            </div>
          </div>
        </div>

        {/* Bloc question — grande carte avec gradient */}
        <div
          className="rounded-3xl bg-gradient-to-br from-card via-card to-red-950/25 border-2 border-red-500/25 p-6 mb-5 shadow-xl"
          data-testid={`quiz-question-${current}`}
        >
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/15 border border-red-500/40 text-[10px] font-black uppercase tracking-widest text-red-300 mb-3">
            <Sparkles className="w-3 h-3" /> Question {current + 1}
          </div>
          <p
            className="text-xl md:text-2xl font-black leading-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {q.q}
          </p>
        </div>

        {/* Grille 2x2 des choix — couleurs vives Kahoot-like */}
        <div className="grid grid-cols-2 gap-2.5" data-testid="quiz-choices">
          {q.choices.map((choice, i) => {
            const letter = ['A', 'B', 'C', 'D'][i];
            const style = CHOICE_STYLES[i] || CHOICE_STYLES[0];
            const hasAnswered = ans !== undefined;
            const isRight = hasAnswered && ans.correct_answer === i;
            const isWrong = hasAnswered && ans.answer === i && !ans.correct;
            const isPicked = hasAnswered && ans.answer === i;
            const isDim = hasAnswered && !isRight && !isPicked;

            let stateClasses;
            if (!hasAnswered) {
              stateClasses = `bg-gradient-to-br ${style.bg} border-2 ${style.border} hover:scale-[1.03] active:scale-[0.97] shadow-lg hover:shadow-2xl`;
            } else if (isRight) {
              stateClasses = 'bg-gradient-to-br from-emerald-500 to-emerald-700 border-2 border-emerald-300 ring-4 ring-emerald-400/40 animate-in zoom-in-95 duration-300';
            } else if (isWrong) {
              stateClasses = 'bg-gradient-to-br from-red-700 to-red-900 border-2 border-red-400 opacity-90';
            } else if (isDim) {
              stateClasses = 'bg-card border-2 border-white/10 opacity-40 grayscale';
            }

            return (
              <button
                key={i}
                type="button"
                onClick={() => submitAnswer(i)}
                disabled={hasAnswered}
                className={`relative rounded-2xl min-h-[110px] p-3.5 flex flex-col items-start justify-between text-left transition-all duration-200 ${stateClasses}`}
                data-testid={`quiz-choice-${i}`}
                aria-label={`Choix ${letter} · ${style.name}`}
              >
                <div className="flex items-center justify-between w-full">
                  <span
                    className={`inline-flex items-center justify-center w-9 h-9 rounded-xl bg-black/25 backdrop-blur-sm text-white font-black text-base border border-white/25`}
                  >
                    {letter}
                  </span>
                  <span className="text-white/70 text-xl font-black" aria-hidden="true">
                    {style.shape}
                  </span>
                </div>
                <span
                  className={`text-sm leading-snug font-bold mt-2 ${
                    isDim ? 'text-foreground/60' : 'text-white'
                  }`}
                  style={{ textShadow: hasAnswered ? 'none' : '0 1px 2px rgba(0,0,0,0.35)' }}
                >
                  {choice}
                </span>
                {/* Overlay feedback icônes */}
                {isRight && (
                  <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white flex items-center justify-center shadow-lg">
                    <Check className="w-4 h-4 text-emerald-600" strokeWidth={3} />
                  </div>
                )}
                {isWrong && (
                  <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white flex items-center justify-center shadow-lg">
                    <XIcon className="w-4 h-4 text-red-600" strokeWidth={3} />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Bandeau feedback + bouton suivant */}
        {ans && (
          <div className="mt-5 space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div
              className={`rounded-2xl border-2 p-4 flex items-start gap-3 ${
                ans.correct
                  ? 'border-emerald-500/50 bg-emerald-500/10'
                  : 'border-orange-500/50 bg-orange-500/10'
              }`}
              data-testid="quiz-feedback"
            >
              <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
                ans.correct ? 'bg-emerald-500' : 'bg-orange-500'
              }`}>
                {ans.correct ? <Check className="w-5 h-5 text-white" strokeWidth={3} /> : <XIcon className="w-5 h-5 text-white" strokeWidth={3} />}
              </div>
              <div className="flex-1">
                <div className={`text-[10px] font-black uppercase tracking-widest mb-0.5 ${
                  ans.correct ? 'text-emerald-300' : 'text-orange-300'
                }`}>
                  {ans.correct ? 'Bonne réponse !' : 'Pas cette fois'}
                </div>
                <div className="text-sm font-bold text-foreground/95 leading-snug">
                  {ans.correct
                    ? 'Bien joué — tu passes à la suite.'
                    : `La bonne réponse était : ${['A','B','C','D'][ans.correct_answer]}.`}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={next}
              className="w-full rounded-2xl bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 active:scale-[0.98] text-white font-black uppercase tracking-widest py-4 text-sm shadow-lg shadow-red-900/30 transition-all inline-flex items-center justify-center gap-2"
              data-testid="quiz-next-btn"
            >
              {current + 1 >= total ? (
                <>
                  <Trophy className="w-4 h-4" /> Voir mon score
                </>
              ) : (
                <>
                  Question suivante <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
